import { computed, type Ref } from 'vue';
import type { ScoreBreakdown } from '~/api/v2/experts/ExpertRepository';

export interface ExplanationItem {
  description: string;
  icon: string;
  iconClass: string;
  score: number;
  isStrength: boolean;
}

const criterionIcons: Record<string, string> = {
  deal_size: 'solar:dollar-linear',
  market_segment: 'solar:chart-linear',
  persona: 'solar:user-rounded-linear',
  industry: 'solar:planet-linear',
  company_size: 'solar:buildings-2-linear',
};

export function useScoreExplanation(
  breakdown: Ref<ScoreBreakdown | undefined>,
  matchScore: Ref<number>,
) {
  const { t } = useI18n();

  function getDescription(criterionType: string, score: number, name: string, reps: number): { text: string; isStrength: boolean } {
    const level = score >= 70 ? 'strong' : score >= 40 ? 'some' : score > 0 ? 'little' : 'none';
    const key = `score_tooltip.${criterionType}_${level}`;
    return {
      text: t(key, { name, count: reps }),
      isStrength: score >= 70,
    };
  }

  const allItems = computed((): ExplanationItem[] => {
    if (!breakdown.value) return [];

    const items: ExplanationItem[] = [];

    // Build items from criteria (skip empty names - criterion not defined on mission)
    for (const criterion of breakdown.value.criteria) {
      if (!criterion.name) continue;

      const { text, isStrength } = getDescription(criterion.criterion_type, criterion.score, criterion.name, criterion.repetitions);

      items.push({
        description: text,
        icon: criterionIcons[criterion.criterion_type] || 'solar:info-circle-linear',
        iconClass: isStrength ? 'text-[#ACFF94]' : 'text-orange-400',
        score: criterion.score,
        isStrength,
      });
    }

    // Add skill coverage if partial
    const { skills_matched, total_required_skills } = breakdown.value;
    if (total_required_skills > 0 && skills_matched < total_required_skills) {
      items.push({
        description: t('score_tooltip.skills_coverage', { matched: skills_matched, total: total_required_skills }),
        icon: 'solar:star-linear',
        iconClass: 'text-orange-400',
        score: (skills_matched / total_required_skills) * 100,
        isStrength: false,
      });
    } else if (total_required_skills > 0 && skills_matched >= total_required_skills) {
      items.push({
        description: t('score_tooltip.skills_full_coverage'),
        icon: 'solar:star-linear',
        iconClass: 'text-[#ACFF94]',
        score: 100,
        isStrength: true,
      });
    }

    return items;
  });

  const explanationItems = computed((): ExplanationItem[] => {
    const items = allItems.value;
    const score = matchScore.value;

    // Selection strategy based on overall score
    if (score >= 70) {
      // High score: show top 3 strengths
      return items
        .filter(i => i.isStrength)
        .sort((a, b) => b.score - a.score)
        .slice(0, 3);
    } else if (score >= 40) {
      // Medium score: best strength + worst weakness + skill coverage or 2nd item
      const strengths = items.filter(i => i.isStrength).sort((a, b) => b.score - a.score);
      const weaknesses = items.filter(i => !i.isStrength).sort((a, b) => a.score - b.score);
      const result: ExplanationItem[] = [];
      if (strengths.length > 0) result.push(strengths[0]);
      if (weaknesses.length > 0) result.push(weaknesses[0]);
      // Fill remaining with next best items
      const used = new Set(result);
      for (const item of [...strengths, ...weaknesses]) {
        if (result.length >= 3) break;
        if (!used.has(item)) {
          result.push(item);
          used.add(item);
        }
      }
      return result;
    } else {
      // Low score: show weaknesses first, then any strength
      const weaknesses = items.filter(i => !i.isStrength).sort((a, b) => a.score - b.score);
      const strengths = items.filter(i => i.isStrength).sort((a, b) => b.score - a.score);
      const result: ExplanationItem[] = [];
      for (const w of weaknesses) {
        if (result.length >= 3) break;
        result.push(w);
      }
      for (const s of strengths) {
        if (result.length >= 3) break;
        result.push(s);
      }
      return result;
    }
  });

  return {
    explanationItems,
  };
}
